import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Heart, Clock, ArrowLeft } from 'lucide-react';
import SongMenu from '../SongMenu';

const ListeningHistory = ({ playSong, likedSongs, toggleLike, user }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchHistory();
  }, [user, navigate]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listening-history/recent?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch listening history:', error);
    } finally {
      setLoading(false);
    }
  };

  const playAll = () => {
    if (history.length > 0) {
      playSong(history[0], history);
    }
  };

  const shuffleAll = () => {
    if (history.length > 0) {
      const shuffled = [...history].sort(() => Math.random() - 0.5);
      playSong(shuffled[0], shuffled);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button 
          className="back-button" 
          onClick={() => navigate(-1)}
          style={{ marginBottom: '24px' }}
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(157, 78, 221, 0.4)'
          }}>
            <Clock size={40} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px' }}>
              Listening History
            </h1>
            <p style={{ fontSize: '16px', color: '#999' }}>
              {history.length} {history.length === 1 ? 'song' : 'songs'} played recently
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {history.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={playAll}
              style={{
                padding: '12px 24px',
                borderRadius: '24px',
                border: 'none',
                background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <Play size={16} fill="white" />
              Play All
            </button>
            <button
              onClick={shuffleAll}
              style={{
                padding: '12px 24px',
                borderRadius: '24px',
                border: '2px solid #9D4EDD',
                background: 'transparent',
                color: '#9D4EDD',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(157, 78, 221, 0.1)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/>
              </svg>
              Shuffle
            </button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <Clock size={48} color="#9D4EDD" style={{ marginBottom: '16px' }} />
          <p>Loading your history...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && history.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <Clock size={64} color="#666" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#fff' }}>
            No Listening History Yet
          </h3>
          <p>Start playing some songs and they'll appear here!</p>
        </div>
      )}

      {/* Songs Table */}
      {!loading && history.length > 0 && (
        <div className="songs-table">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #333' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px', width: '50px' }}>#</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px' }}>Title</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px' }}>Album</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#999', fontWeight: '500', fontSize: '14px', width: '100px' }}>Duration</th>
                <th style={{ padding: '12px', textAlign: 'right', color: '#999', fontWeight: '500', fontSize: '14px', width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.map((song, index) => (
                <tr 
                  key={`${song.id}-${index}`}
                  style={{ 
                    borderBottom: '1px solid #2a2a2a',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '12px', color: '#999', fontSize: '14px' }}>
                    {index + 1}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div 
                      onClick={() => playSong(song, history)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      <img
                        src={song.albumArt || song.album_art_url}
                        alt={song.title}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '4px',
                          objectFit: 'cover'
                        }}
                      />
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '500', color: '#fff', marginBottom: '2px' }}>
                          {song.title}
                        </div>
                        <div style={{ fontSize: '13px', color: '#999' }}>
                          {song.artist}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ccc', fontSize: '14px' }}>
                    {song.album}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                    {Math.floor(song.duration / 60)}:{String(song.duration % 60).padStart(2, '0')}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLike(song.id);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Heart
                          size={18}
                          fill={likedSongs.has(song.id) ? '#9D4EDD' : 'none'}
                          color={likedSongs.has(song.id) ? '#9D4EDD' : '#999'}
                        />
                      </button>
                      <SongMenu song={song} user={user} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListeningHistory;
